import { fetch, fetchScript, ResponseError, getURLParameter, initTooltips } from "@/utilities";
import { html, render } from "lit";
import { Modal } from "bootstrap";
import { searchQueryState } from "@/state/SearchQuery";
import { I18n } from "@/i18n/i18n";
import { RadioCards } from "@/components/radio_cards";

function loadUsers(_status = undefined): void {
    const status = _status || getURLParameter("status");
    searchQueryState.queryParams.set("status", status);
}

function initCourseMembers(): void {
    function init(): void {
        initLabelsEditModal();
    }
    function initLabelsEditModal(): void {
        document.getElementById("labelsUploadButton").addEventListener("click", () => void uploadLabels());
    }

    async function uploadLabels(): Promise<void> {
        const modal = document.getElementById("labelsUploadModal");
        modal.querySelectorAll(".alert").forEach((al) => al.remove());
        const input = document.getElementById("newCsvFileInput") as HTMLInputElement;
        const formData = new FormData();
        formData.append("file", input.files[0]);
        try {
            await fetch(`/courses/${modal.dataset.course_id}/members/upload_labels_csv`, {
                method: "POST",
                body: formData,
                headers: { Accept: "application/json" },
            });
            loadUsers();
            Modal.getOrCreateInstance(modal).hide();
        } catch (error) {
            if (error instanceof ResponseError) {
                const data = await error.response.json();
                const alertEl = html`
                    <div class="alert alert-danger alert-dismissible">
                        <button type="button" class="btn-close" data-bs-dismiss="alert"></button>${data.message}
                    </div>
                `;
                const modalBody = modal.getElementsByClassName("modal-body")[0] as HTMLElement;
                render(alertEl, modalBody, { renderBefore: modalBody.firstChild });
            } else {
                throw error;
            }
        }
    }

    init();
}

function initCourseForm(): void {
    function init(): void {
        initInstitutionRelatedSelects();
        initKindSelector();
        initTooltips();
        initDraftAnswerToggle();
    }

    function initKindSelector(): void {
        const kindInput = document.getElementById("course_kind") as HTMLInputElement | null;
        const featuredCheckbox = document.getElementById("course_featured") as HTMLInputElement | null;
        const draftCheckbox = document.getElementById("course_draft") as HTMLInputElement | null;
        if (!kindInput || !draftCheckbox) {
            return;
        }

        function applyDraftVisibility(isDraft: boolean): void {
            document.querySelectorAll<HTMLElement>(".course-draft-disabled").forEach((row) => {
                row.querySelectorAll<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>(
                    "input:not([type=hidden]), select, textarea",
                ).forEach((el) => {
                    el.disabled = isDraft;
                });
            });
            document.querySelectorAll<HTMLElement>(".course-draft-only").forEach((el) => {
                el.classList.toggle("hidden", !isDraft);
            });

            const academicYearSelect = document.getElementById("course_academic_year_id") as HTMLSelectElement | null;
            if (academicYearSelect) {
                let blankOption = academicYearSelect.querySelector<HTMLOptionElement>("option[value='']");
                if (isDraft) {
                    // A draft course can't have an academic year. Remember the choice so it can be
                    // restored, and show the "no academic year" option (creating it if the form
                    // didn't render one - it isn't offered on a regular course).
                    academicYearSelect.dataset.previousValue = academicYearSelect.value;
                    if (!blankOption) {
                        blankOption = new Option(academicYearSelect.dataset.noAcademicYearLabel ?? "", "");
                        blankOption.dataset.injected = "true";
                        academicYearSelect.prepend(blankOption);
                    }
                    academicYearSelect.value = "";
                } else {
                    if (blankOption?.dataset.injected) blankOption.remove();
                    if (academicYearSelect.dataset.previousValue !== undefined) {
                        academicYearSelect.value = academicYearSelect.dataset.previousValue;
                        delete academicYearSelect.dataset.previousValue;
                    }
                }
            }

            const questionsCheckbox = document.getElementById("course_questions_enabled") as HTMLInputElement | null;
            if (questionsCheckbox) {
                if (isDraft) {
                    // A draft course is hidden from students, so questions are always off. Remember
                    // the choice so it can be restored if the draft checkbox is unchecked again.
                    questionsCheckbox.dataset.previousChecked = String(questionsCheckbox.checked);
                    questionsCheckbox.checked = false;
                } else if (questionsCheckbox.dataset.previousChecked !== undefined) {
                    questionsCheckbox.checked = questionsCheckbox.dataset.previousChecked === "true";
                    delete questionsCheckbox.dataset.previousChecked;
                }
            }

            const fieldset = document.getElementById("access-rights-fieldset") as HTMLFieldSetElement | null;
            if (fieldset) {
                fieldset.disabled = isDraft;
            }

            if (isDraft) {
                const hiddenRadio = document.querySelector<HTMLInputElement>(
                    "input[name='course[visibility]'][value='hidden']",
                );
                const moderatedRadio = document.querySelector<HTMLInputElement>(
                    "input[name='course[registration]'][value='moderated']",
                );
                if (hiddenRadio) hiddenRadio.checked = true;
                if (moderatedRadio) moderatedRadio.checked = true;
            }
        }

        function sync(): void {
            const kind = draftCheckbox.checked ? "draft" : featuredCheckbox?.checked ? "featured" : "regular";
            kindInput.value = kind;
            applyDraftVisibility(kind === "draft");
            document.querySelectorAll<HTMLElement>(".course-featured-hidden").forEach((el) => {
                el.classList.toggle("hidden", kind === "featured");
            });
        }

        draftCheckbox.addEventListener("change", () => {
            if (draftCheckbox.checked && featuredCheckbox) featuredCheckbox.checked = false;
            sync();
        });
        featuredCheckbox?.addEventListener("change", () => {
            if (featuredCheckbox.checked) draftCheckbox.checked = false;
            sync();
        });
    }

    function initInstitutionRelatedSelects(): void {
        const institutionSelect = document.getElementById("course_institution_id") as HTMLInputElement;
        const visibleForAll = document.getElementById("course_visibility_visible_for_all") as HTMLInputElement;
        const visibleForInstitution = document.getElementById(
            "course_visibility_visible_for_institution",
        ) as HTMLInputElement;

        function changeListener(): void {
            if (!institutionSelect.value) {
                if (visibleForInstitution.checked) {
                    visibleForAll.checked = true;
                }

                visibleForInstitution.disabled = true;
                document.querySelectorAll(".fill-institution").forEach((el) => {
                    el.innerHTML = I18n.t("js.configured-institution");
                });
            } else {
                visibleForInstitution.removeAttribute("disabled");
                document.querySelectorAll(".fill-institution").forEach((el) => {
                    el.innerHTML = institutionSelect.querySelector("option:checked").innerHTML;
                });
            }
        }

        setTimeout(changeListener);
        institutionSelect.addEventListener("change", changeListener);
    }

    function initDraftAnswerToggle(): void {
        const questionToggle = document.getElementById("course_questions_enabled") as HTMLInputElement;
        const draftAnswerToggle = document.getElementById("course_draft_answers_enabled") as HTMLInputElement;

        questionToggle.addEventListener("change", () => {
            draftAnswerToggle.disabled = !questionToggle.checked;
            if (!questionToggle.checked) {
                draftAnswerToggle.checked = false;
            }
        });
    }

    init();
}

function initCourseNew(): void {
    function init(): void {
        initPanelLogic();
        window.dodona.courseFormLoaded = courseFormLoaded;
        window.dodona.copyCoursesLoaded = copyCoursesLoaded;

        // Bootstrap's automatic collapsing of other elements in the parent breaks
        // when doing manual shows and hides, so we have to do this.
        typeCollapseElement.addEventListener("show.bs.collapse", () => {
            chooseCollapse.hide();
            formCollapse.hide();
        });
        chooseCollapseElement.addEventListener("show.bs.collapse", () => {
            typeCollapse.hide();
            formCollapse.hide();
        });
        formCollapseElement.addEventListener("show.bs.collapse", () => {
            typeCollapse.hide();
            chooseCollapse.hide();
        });
    }

    const typePanel = document.getElementById("type-panel");
    const typeCollapseElement = typePanel.querySelector(".panel-collapse");
    const typeCollapse = new bootstrap.Collapse(typeCollapseElement, { toggle: false });

    const choosePanel = document.getElementById("choose-panel");
    const chooseCollapseElement = choosePanel.querySelector(".panel-collapse");
    const chooseCollapse = new bootstrap.Collapse(chooseCollapseElement, { toggle: false });

    const formPanel = document.getElementById("form-panel");
    const formCollapseElement = formPanel.querySelector(".panel-collapse");
    const formCollapse = new bootstrap.Collapse(formCollapseElement, { toggle: false });

    function initPanelLogic(): void {
        const typeSelector = document.getElementById("course-type-selector") as RadioCards;
        typeSelector.addEventListener("change", () => {
            typePanel.querySelector(".answer").textContent = typeSelector.selectedOption?.label ?? "";

            if (typeSelector.value === "copy") {
                choosePanel.classList.remove("hidden");
                chooseCollapse.show();
                choosePanel.querySelectorAll<HTMLInputElement>(`input[type="radio"]`).forEach((el) => {
                    el.checked = false;
                });
                formPanel.classList.add("hidden");
                formPanel.querySelector(".step-circle").innerHTML = "3";
            } else {
                choosePanel.classList.add("hidden");
                formPanel.querySelector(".step-circle").innerHTML = "2";
                void fetchScript("/courses/new.js");
            }
        });
    }

    function copyCoursesLoaded(): void {
        document.querySelectorAll("[data-course_id]").forEach((el: HTMLElement) => {
            el.addEventListener("click", function () {
                (el.querySelector(`input[type="radio"]`) as HTMLInputElement).checked = true;
                el.closest(".panel").querySelector(".answer").textContent = el.dataset.answer;
                void fetchScript(`/courses/new.js?copy_options[base_id]=${el.dataset.course_id}`);
            });
        });

        document.querySelectorAll(".copy-course-row .nested-link").forEach((el) => {
            el.addEventListener("click", (event) => {
                event.stopPropagation();
            });
        });
    }

    function courseFormLoaded(): void {
        formPanel.classList.remove("hidden");
        formCollapse.show();
        window.scrollTo(0, 0);
    }

    init();
}

export { initCourseForm, initCourseNew, initCourseMembers };
